import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Swal from 'sweetalert2';

const schema = yup.object().shape({
    title: yup.string().required('Title is required'),
    price: yup
        .number()
        .typeError('Price must be a number')
        .positive('Price must be positive')
        .required('Price is required'),
    width: yup
        .number()
        .typeError('Width must be a number')
        .positive('Width must be positive')
        .required('Width is required'),
    length: yup
        .number()
        .typeError('Length must be a number')
        .positive('Length must be positive')
        .required('Length is required'),
    status: yup.string().oneOf(['active', 'inactive']).required()
});

const AddNewPackage = () => {
    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            title: '',
            price: '',
            width: '',
            length: '',
            status: 'active'
        }
    });

    const onSubmit = async (data) => {
        try {
            // Calculate total area (width * length) before submitting

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/packages`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error('Failed to create package');
            }

            const result = await response.json();
            Swal.fire({
                title: "Success!",
                text: "Package created successfully.",
                icon: "success",
                confirmButtonColor: "#2563eb",
            });

            reset();
        } catch (error) {
            console.error('Error:', error);
            alert('Error creating package');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-3">
                        Create New Package
                    </h1>
                    <p className="text-lg text-gray-600">
                        Design a beautiful package that your customers will love
                    </p>
                </div>

                <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl">
                    <div className="p-8 sm:p-10">
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            {/* Title Field */}
                            <div className="space-y-2">
                                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                                    Package Title <span className="text-red-500">*</span>
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        id="title"
                                        {...register('title')}
                                        className={`block w-full px-4 py-3 rounded-lg border ${errors.title ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                            } focus:ring-2 focus:ring-opacity-50 transition duration-200`}
                                        placeholder="Premium Membership"
                                    />
                                    {errors.title && (
                                        <p className="mt-1 text-sm text-red-600 animate-fade-in">
                                            {errors.title.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Price Field */}
                            <div className="space-y-2">
                                <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                                    Price <span className="text-red-500">*</span>
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-gray-500">₹</span>
                                    </div>
                                    <input
                                        id="price"
                                        {...register('price')}
                                        className={`block w-full pl-9 pr-4 py-3 rounded-lg border ${errors.price ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                            } focus:ring-2 focus:ring-opacity-50 transition duration-200`}
                                        placeholder="0.00"
                                    />
                                    {errors.price && (
                                        <p className="mt-1 text-sm text-red-600 animate-fade-in">
                                            {errors.price.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Plot Dimensions */}
                            <div className="space-y-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    Plot Dimensions ( feet) <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Width Field */}
                                    <div className="space-y-2">
                                        <label htmlFor="width" className="block text-sm font-medium text-gray-700">
                                            Width
                                        </label>
                                        <div className="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="width"
                                                type="number"
                                                {...register('width')}
                                                className={`block w-full px-4 py-3 rounded-lg border ${errors.width ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                    } focus:ring-2 focus:ring-opacity-50 transition duration-200`}
                                                placeholder="Width in  feet"
                                            />
                                            {errors.width && (
                                                <p className="mt-1 text-sm text-red-600 animate-fade-in">
                                                    {errors.width.message}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Length Field */}
                                    <div className="space-y-2">
                                        <label htmlFor="length" className="block text-sm font-medium text-gray-700">
                                            Length
                                        </label>
                                        <div className="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="length"
                                                type="number"
                                                {...register('length')}
                                                className={`block w-full px-4 py-3 rounded-lg border ${errors.length ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                    } focus:ring-2 focus:ring-opacity-50 transition duration-200`}
                                                placeholder="Length in  feet"
                                            />
                                            {errors.length && (
                                                <p className="mt-1 text-sm text-red-600 animate-fade-in">
                                                    {errors.length.message}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Status Select */}
                            <div className="space-y-2">
                                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                                    Package Status
                                </label>
                                <div className="mt-1">
                                    <Controller
                                        name="status"
                                        control={control}
                                        render={({ field }) => (
                                            <select
                                                id="status"
                                                {...field}
                                                className="block w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-opacity-50 transition duration-200"
                                            >
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full flex justify-center items-center py-3 px-6 border border-transparent rounded-lg shadow-sm text-lg font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 transition duration-300 transform hover:scale-[1.02]"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Creating Package...
                                        </>
                                    ) : (
                                        'Create Package'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Custom Animations */}
            <style jsx global>{`
                @keyframes fade-in {
                    from { opacity: 0; transform: translateY(-5px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in {
                    animation: fade-in 0.3s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default AddNewPackage;